<template>
    <div class="dsl-root" data-testid="dsl-root">
        <header class="dsl-header">
            <div>
                <h2 class="dsl-header__title">Decoration sets</h2>
                <p class="dsl-header__sub">
                    Reusable placement bundles -- save a configured set
                    once, apply it to many products.
                </p>
            </div>
            <!--
                Issue #2812: a "+ New set" button lived here bound to an
                empty handler, so clicking it did nothing. Sets are
                created from the product editor's "Save as set", which
                is the truthful path. Restore this affordance when a
                standalone create surface actually ships.
            -->
        </header>

        <div class="dsl-controls">
            <input
                type="text"
                class="dsl-search"
                placeholder="Search by name..."
                data-testid="dsl-search"
                :value="searchText"
                @input="onSearchInput"
                @change="onSearchCommit"
                @keyup.enter="onSearchCommit"
            />
            <div
                v-if="availableTags.length"
                class="dsl-filter-tags"
                role="group"
                aria-label="Filter by client tag"
            >
                <span class="dsl-filter-tags__label">Client tag:</span>
                <button
                    v-for="tag in availableTags"
                    :key="tag"
                    type="button"
                    class="dsl-filter-tag"
                    :class="{ 'dsl-filter-tag--active': activeTag === tag }"
                    :data-testid="`dsl-filter-tag-${tag}`"
                    @click="onToggleTag(tag)"
                >
                    {{ tag }}
                </button>
                <button
                    v-if="activeTag"
                    type="button"
                    class="dsl-filter-tag dsl-filter-tag--clear"
                    data-testid="dsl-filter-tag-clear"
                    @click="onClearTag"
                >
                    Clear
                </button>
            </div>
        </div>

        <div
            v-if="deleteError"
            class="dsl-state dsl-state--error"
            data-testid="dsl-delete-error"
        >
            {{ deleteError }}
        </div>

        <div
            v-if="store.loading"
            class="dsl-state dsl-state--loading"
            data-testid="dsl-loading"
        >
            Loading sets...
        </div>
        <div
            v-else-if="store.error"
            class="dsl-state dsl-state--error"
            data-testid="dsl-error"
        >
            {{ store.error }}
        </div>
        <div
            v-else-if="!store.sets.length"
            class="dsl-state dsl-state--empty"
            data-testid="dsl-empty"
        >
            No decoration sets yet. Save your first set from a configured
            product to reuse it later.
        </div>
        <ul
            v-else
            class="dsl-list"
            data-testid="dsl-list"
        >
            <li
                v-for="set in store.sets"
                :key="set.id"
                class="dsl-row"
                :data-testid="`dsl-row-${set.id}`"
                tabindex="0"
                role="button"
                @click="onRowClick(set)"
                @keyup.enter="onRowClick(set)"
                @keyup.space.prevent="onRowClick(set)"
            >
                <div class="dsl-row__main">
                    <span class="dsl-row__name">{{ set.name }}</span>
                    <span
                        class="dsl-row__count"
                        :data-testid="`dsl-placement-count-${set.id}`"
                    >
                        {{ formatPlacementCount(set) }}
                    </span>
                    <span
                        class="dsl-row__used"
                        :data-testid="`dsl-used-by-${set.id}`"
                    >
                        used by {{ usedByCount(set) }} products
                    </span>
                </div>
                <div class="dsl-row__meta">
                    <span
                        v-if="set.client_tag"
                        class="dsl-chip"
                        :data-testid="`dsl-client-tag-${set.id}`"
                    >
                        {{ set.client_tag }}
                    </span>
                    <button
                        type="button"
                        class="dsl-row__delete"
                        :data-testid="`dsl-delete-${set.id}`"
                        :disabled="deletingId === set.id"
                        aria-label="Delete decoration set"
                        title="Delete decoration set"
                        @click.stop="onDeleteSet(set)"
                        @keyup.enter.stop="onDeleteSet(set)"
                    >
                        Delete
                    </button>
                </div>
            </li>
        </ul>

        <nav
            v-if="totalPages > 1"
            class="dsl-pagination"
            aria-label="Pagination"
            data-testid="dsl-pagination"
        >
            <button
                type="button"
                class="dsl-btn dsl-btn--ghost"
                data-testid="dsl-page-prev"
                :disabled="store.page <= 1"
                @click="goToPage(store.page - 1)"
            >
                Previous
            </button>
            <span
                class="dsl-pagination__status"
                data-testid="dsl-pagination-status"
            >
                Page {{ store.page }} of {{ totalPages }}
                ({{ store.total }} sets)
            </span>
            <button
                type="button"
                class="dsl-btn dsl-btn--ghost"
                data-testid="dsl-page-next"
                :disabled="store.page >= totalPages"
                @click="goToPage(store.page + 1)"
            >
                Next
            </button>
        </nav>
    </div>
</template>

<script>
/**
 * DecorationSetsListPage.vue -- /distributor/decorations/sets.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.8 -- list surface at /decorations/sets.
 *
 * Per P4-Q18: Vuetify-style pagination at 30/page + name search +
 * client-tag filter chips. The chips render only for tags actually
 * present in the current list page; a "Clear" affordance appears once
 * a tag is active.
 *
 * Capability: distributor.decoration_library.read (gate enforced by the
 * router at ``router/index.js`` -- this component does NOT re-check).
 *
 * "Used by N products" column: the backend doesn't yet track per-set
 * apply history (ADR 0044 reserves it but P4.1 didn't ship the audit
 * table). We render ``used by 0 products`` for every row until that
 * follow-up lands -- the placement-count badge carries the meaningful
 * signal in the meantime. The hook is centralised in ``usedByCount``
 * so the followup PR has a single point of change.
 *
 * Search + filter UX: the search input commits on Enter or blur (not
 * on every keystroke) to avoid hammering the API on each character.
 * Tag chips are click-to-toggle; only one tag is active at a time
 * (matches the single-param exact-match the backend supports).
 *
 * Navigation: row click routes to /distributor/decorations/sets/<id>
 * which renders ``DecorationSetDetailPage.vue``.
 */
import { useDecorationSetsStore } from '@/stores/decorationSets'

export default {
    name: 'DecorationSetsListPage',

    data() {
        return {
            store: useDecorationSetsStore(),
            searchText: '',
            activeTag: null,
            deletingId: null,
            deleteError: null,
        }
    },

    computed: {
        totalPages() {
            const t = this.store.total
            const p = this.store.perPage
            if (!t || !p) return 1
            return Math.max(1, Math.ceil(t / p))
        },

        /**
         * Tags surfaced in the current page. We could fetch the
         * distinct tag set from a sidecar endpoint, but that's
         * Phase 4.10+ scope. Rendering tags from the current page
         * keeps the filter discoverable without a separate roundtrip.
         */
        availableTags() {
            const seen = new Set()
            for (const s of this.store.sets || []) {
                if (typeof s.client_tag === 'string' && s.client_tag.trim()) {
                    seen.add(s.client_tag.trim())
                }
            }
            return Array.from(seen).sort()
        },
    },

    async mounted() {
        await this.store.fetchList({ page: 1 })
    },

    methods: {
        formatPlacementCount(set) {
            const n = Number.isFinite(set?.placement_count)
                ? set.placement_count
                : (Array.isArray(set?.placements) ? set.placements.length : 0)
            return n === 1 ? '1 placement' : `${n} placements`
        },

        /**
         * Reserved for the "used by N products" backend wiring.
         *
         * P4.1 didn't ship the apply-audit table; ADR 0044 reserves
         * the surface. Until the audit table lands the column reads
         * zero. The centralised helper keeps the followup PR's diff
         * minimal -- swap the return for ``set.applied_product_count``
         * (or whatever shape the audit endpoint surfaces).
         */
        usedByCount(set) {
            return Number.isFinite(set?.applied_product_count)
                ? set.applied_product_count
                : 0
        },

        onSearchInput(event) {
            this.searchText = event?.target?.value ?? ''
        },

        async onSearchCommit() {
            await this.store.fetchList({
                page: 1,
                name: this.searchText,
                clientTag: this.activeTag,
            })
        },

        async onToggleTag(tag) {
            this.activeTag = this.activeTag === tag ? null : tag
            await this.store.fetchList({
                page: 1,
                name: this.searchText,
                clientTag: this.activeTag,
            })
        },

        async onClearTag() {
            this.activeTag = null
            await this.store.fetchList({
                page: 1,
                name: this.searchText,
                clientTag: null,
            })
        },

        async goToPage(page) {
            if (page < 1 || page > this.totalPages) return
            await this.store.fetchList({ page })
        },

        onRowClick(set) {
            this.$router.push(
                `/distributor/decorations/sets/${set.id}`,
            )
        },

        /**
         * Soft-delete (archive) a set from the list.
         *
         * Confirms first (window.confirm, guarded for SSR / test envs
         * that stub it), then delegates to the store's ``deleteSet``
         * action which hits DELETE /decoration_sets/<id>/ and prunes the
         * row from the list cache on success. ``@click.stop`` on the
         * button keeps the row-navigation handler from firing.
         */
        async onDeleteSet(set) {
            if (!set || set.id == null) return
            if (this.deletingId != null) return
            const ok =
                typeof window !== 'undefined'
                && typeof window.confirm === 'function'
                    ? window.confirm('Delete this decoration set?')
                    : true
            if (!ok) return
            this.deleteError = null
            this.deletingId = set.id
            try {
                await this.store.deleteSet(set.id)
            } catch (e) {
                this.deleteError =
                    e?.response?.data?.error
                    || e?.message
                    || 'Delete failed.'
            } finally {
                this.deletingId = null
            }
        },
    },
}
</script>

<style scoped>
.dsl-root {
    width: 100%;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.dsl-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.dsl-header__title {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
}

.dsl-header__sub {
    margin: 4px 0 0;
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
}

.dsl-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
}

.dsl-search {
    flex: 0 0 280px;
    padding: 8px 10px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.dsl-filter-tags {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.dsl-filter-tags__label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.dsl-filter-tag {
    border: 1px solid rgba(25, 118, 210, 0.5);
    background: #fff;
    color: #1976d2;
    border-radius: 14px;
    padding: 4px 12px;
    font-size: 12px;
    cursor: pointer;
}

.dsl-filter-tag--active {
    background: #1976d2;
    color: #fff;
}

.dsl-filter-tag--clear {
    border-color: rgba(0, 0, 0, 0.2);
    color: rgba(0, 0, 0, 0.6);
}

.dsl-state {
    padding: 40px 24px;
    text-align: center;
    color: rgba(0, 0, 0, 0.55);
    border: 1px dashed rgba(0, 0, 0, 0.15);
    border-radius: 6px;
}

.dsl-state--error {
    color: #d32f2f;
    border-color: rgba(211, 47, 47, 0.4);
}

.dsl-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    background: #fff;
}

.dsl-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    cursor: pointer;
}

.dsl-row:last-child {
    border-bottom: 0;
}

.dsl-row:hover,
.dsl-row:focus {
    background: rgba(25, 118, 210, 0.06);
    outline: none;
}

.dsl-row__main {
    display: flex;
    align-items: baseline;
    gap: 14px;
    min-width: 0;
}

.dsl-row__name {
    font-weight: 500;
    font-size: 15px;
}

.dsl-row__count,
.dsl-row__used {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
}

.dsl-chip {
    font-size: 11px;
    padding: 2px 10px;
    border-radius: 10px;
    background: rgba(25, 118, 210, 0.12);
    color: #1565c0;
}

.dsl-row__meta {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dsl-row__delete {
    border: 1px solid rgba(211, 47, 47, 0.4);
    background: #fff;
    color: #d32f2f;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
}

.dsl-row__delete:hover {
    background: rgba(211, 47, 47, 0.08);
}

.dsl-row__delete:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.dsl-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding-top: 8px;
}

.dsl-pagination__status {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
}

.dsl-btn {
    border-radius: 4px;
    border: 1px solid transparent;
    padding: 6px 14px;
    cursor: pointer;
    background: #fff;
    color: #1976d2;
    font-size: 14px;
}

.dsl-btn--primary {
    background: #1976d2;
    color: #fff;
    border-color: #1976d2;
}

.dsl-btn--ghost {
    background: #fff;
    color: #1976d2;
    border-color: rgba(25, 118, 210, 0.4);
}

.dsl-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
